import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import { DataTableDirective } from 'angular-datatables';
import { FlashMessagesService } from 'flash-messages-angular';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { DatatablesHelper } from 'src/app/helpers';
import { AdminService } from 'src/app/services/admin.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-admin-banned-list',
  templateUrl: './admin-banned-list.component.html',
  styleUrls: ['./admin-banned-list.component.css']
})
export class AdminBannedListComponent implements OnInit, OnDestroy {
  bans: any[] = [];
  loaded = false;

  form!: FormGroup;
  submitting = false;

  get email() { return this.form.get('email') as FormControl; }

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the list of banned emails can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    public auth: AuthService,
    private adminService: AdminService,
    private flashMessage: FlashMessagesService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.initTable();
    this.loadBannedList();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initForm(): void {
    // Initialize formgroup
    this.form = new FormGroup({
      email: new FormControl()
    });
  }

  private initTable(): void {
    this.dtOptions = {
      // pagingType: 'full_numbers',
      pageLength: 100000,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      // dom: 'lBfrtip',
      dom: 'Bfti',

      order: []
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'excel'
      ]
    });
  }

  private async loadBannedList() {
    const res: any = await this.adminService.getBannedList({ sort: 'registered' }).toPromise();

    this.bans = res.list;

    this.loadTable();
  }

  private loadTable() {
    if (this.loaded) {
      this.rerender();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  private async tryReloadTable() {
    await this.loadBannedList().catch(() => {});
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  async removeBan(index: number) {
    const entry = this.bans[index];
    const email = entry.email;

    if (confirm(`Are you sure to remove ${email} from the banned list?`) === false) {
      return;
    }

    // Remove Ban
    const data: any = await this.adminService.unbanUser(email).toPromise();

    // Reload table data
    await this.tryReloadTable();

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("Email removed from banned list.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async addBan(email: string) {
    // Add Ban
    const data: any = await this.adminService.banUser(email).toPromise();

    // Reload table data
    await this.tryReloadTable();

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("Email added to banned list.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async onSubmit(f: any) {
    if (this.submitting) {
      return false;
    }

    const form = f as NgForm;

    this.submitting = true;

    try {
      const value = this.form.value;

      await this.addBan(value.email);

      form.resetForm();

      return true;
    } finally {
      this.submitting = false;
    }
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
